import { DotLegalCheckbox, DotLegalDeleteDialog } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../localization/useTranslation.ts";
import { AccessControlEntity, useAccessResponsibilityHelper } from "./useAccessResponsibilityHelper.hooks.ts";
import { Trans } from "react-i18next";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { useGroupEntitiesQuery } from "../../processingActivity/hooks/useGroupEntitiesQuery.ts";

export interface VisibleToAllUsersFieldProps<T extends AccessControlEntity> {
    entity: T | undefined;
    onChange: (changeFunc: (entity: T) => void) => Promise<void>;
    onConfirmNavigate: () => void;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
    toolTipText?: string;
    disableAccessWarning?: boolean;
    confirmAccessRemovalKey: string;
}

function VisibleToAllUsersField<T extends AccessControlEntity>(props: VisibleToAllUsersFieldProps<T>) {
    const { translateString } = useTranslation();
    const groupEntityQuery = useGroupEntitiesQuery(!props.isPreview);
    const hasMultipleGroupEntities = (groupEntityQuery.allGroupEntities?.length ?? 0) > 1;
    const label = translateString(hasMultipleGroupEntities ? "visibleToAllUsers" : "accessibleToAllUsers");
    const { onFieldChange, handleDialogConfirm, pendingUserChanges, setPendingUserChanges } = useAccessResponsibilityHelper<T>(
        props.onChange,
        props.onConfirmNavigate,
        props.disableAccessWarning
    );

    return (
        <>
            <DotLegalCheckbox
                checked={props.entity?.visibleToAllUsers ?? false}
                label={label}
                margin={{ marginLeft: -1.5 }}
                tooltip={props.toolTipText}
                onChange={(value) => {
                    let newEntity = { ...props.entity!, visibleToAllUsers: value };
                    onFieldChange(newEntity, props.entity!);
                }}
                disabled={props.disabled}
                error={!isNullOrWhitespace(props.errorText)}
                helperText={props.errorText}
            />

            {pendingUserChanges && (
                <DotLegalDeleteDialog
                    open
                    headerText={translateString("confirmAccessRemoval")}
                    alertBodyText={
                        <Trans
                            i18nKey={props.confirmAccessRemovalKey}
                            components={{
                                ul: <ul />,
                                li: <li />,
                            }}
                        />
                    }
                    warningTranslation={translateString("warning")}
                    deleteBtnText={translateString("save")}
                    cancelBtnText={translateString("cancel")}
                    onDialogClose={() => setPendingUserChanges(null)}
                    onOkClick={() => handleDialogConfirm()}
                />
            )}
        </>
    );
}

export default VisibleToAllUsersField;
